<template>
    <VJoyPanel title-size="small">
        <template #panel-body>
            <div class="simple-report__input">
                <VJoyInput
                    v-model.number="r$.$value"
                    :label="label"
                    data-testid="number-of-work-amount"
                    :invalid="r$.$error"
                    name="number-of-work-amount"
                    :placeholder="0"
                    type="number"
                />

                <VJoyFormError v-if="r$.$error" :errors="r$.$errors" />
            </div>

            <div class="simple-report__divider" />
            <VJoyWrapper justify="space-between" class="simple-report__body-wrapper">
                <div>{{ isInDays ? t('activity-tracker.simple.daily.rate') : t('activity-tracker.simple.hourly.rate') }}</div>
                <div class="simple-report__earnings">{{ formatAmount(currency!, dailyRate!, {decimals: 2}) }}</div>
            </VJoyWrapper>
            <div class="simple-report__divider" />
            <VJoyWrapper justify="space-between" class="simple-report__body-wrapper">
                <div>{{ t('activity-tracker.simple.total') }}</div>
                <div class="simple-report__earnings">{{ formatAmount(currency!, totalAmountExcludingTaxes!, {decimals: 2}) }}</div>
            </VJoyWrapper>
            <VJoyWrapper
                v-for="taxWithAmount in taxesWithAmountValues"
                :key="taxWithAmount.type"
                justify="space-between"
                class="simple-report__body-wrapper taxes-breakdown"
            >
                <div>{{ t(`activity-tracker.simple.total.taxes.${taxWithAmount.type!}`, [taxWithAmount.percent]) }}</div>
                <div class="simple-report__earnings">{{ formatAmount(currency!, taxWithAmount.amount, {decimals: 2}) }}</div>
            </VJoyWrapper>
            <VJoyWrapper
                v-if="taxesWithAmountValues != null && taxesWithAmountValues.length > 1"
                justify="space-between"
                class="simple-report__body-wrapper taxes-breakdown-total"
            >
                <div>{{ t('activity-tracker.simple.total.taxes', [vatRate]) }}</div>
                <div class="simple-report__earnings">{{ formatAmount(currency!, totalAmountTaxes!, {decimals: 2}) }}</div>
            </VJoyWrapper>
            <VJoyWrapper justify="space-between" class="simple-report__body-wrapper total-including-taxes">
                <div v-html="t('activity-tracker.simple.total.amount.invoiced')" />
                <div class="simple-report__earnings">{{ formatAmount(currency!, totalAmountIncludingTaxes!, {decimals: 2}) }}</div>
            </VJoyWrapper>
            <div class="simple-report__divider" />
            <VJoyWrapper justify="space-between" class="simple-report__body-wrapper">
                <div>{{ t('activity-tracker.simple.malt.service.fees') }}</div>
                <div class="simple-report__earnings">{{ formatAmount(currency!, freelanceFeesExcludingTaxes!, {decimals: 2}) }}</div>
            </VJoyWrapper>
            <VJoyWrapper justify="space-between" class="simple-report__body-wrapper">
                <div>{{ t('activity-tracker.simple.malt.service.fees.taxes', [feeVatRate]) }}</div>
                <div class="simple-report__earnings">{{ formatAmount(currency!, freelanceFeesTaxes!, {decimals: 2}) }}</div>
            </VJoyWrapper>
            <div class="simple-report__divider" />

            <VJoyWrapper
                v-if="totalAmountInFreelancerCurrency"
                justify="space-between"
                class="simple-report__body-wrapper invoicedInCurrency-wrapper"
            >
                <div
                    class="simple-report__total-earnings"
                    v-html="t('activity-tracker.simple.multi.currency.invoiced', {0: getCurrencySymbolFrom(currency!)})"
                />
                <div class="simple-report__earnings">
                    {{ formatAmount(currency!, amountExcludingServiceFeesIncludingTaxes!, {decimals: 2}) }}
                </div>
            </VJoyWrapper>

            <VJoyWrapper
                v-if="totalAmountInFreelancerCurrency"
                justify="space-between"
                direction="column"
                align="stretch"
                class="simple-report__body-wrapper total-wrapper"
            >
                <div class="simple-report__multi-currency">
                    <div class="simple-report__total-earnings" v-html="t('activity-tracker.simple.multi.currency.approximately')" />
                    <div class="simple-report__earnings">
                        ≈ {{ formatAmount(freelancerCurrency!, totalAmountInFreelancerCurrency!, {decimals: 2}) }}
                    </div>
                </div>
                <div
                    class="simple-report__earnings-info"
                    v-html="t('activity-tracker.multi.currency.earnings.info', {0: formatAmount(currency!, estimationExchangeRate!, {decimals: 2})})"
                />
            </VJoyWrapper>

            <VJoyWrapper v-if="!totalAmountInFreelancerCurrency" justify="space-between" class="simple-report-panel__body-wrapper total-wrapper">
                <div class="simple-report__total-earnings" v-html="t('activity-tracker.simple.total.earnings')" />
                <div class="simple-report__earnings">
                    {{ formatAmount(currency!, amountExcludingServiceFeesIncludingTaxes!, {decimals: 2}) }}
                </div>
            </VJoyWrapper>

            <div class="simple-report__divider" />

            <VJoyTextarea
                v-model="workDescription"
                optional-label="optional"
                :label="t('activity-tracker.simple.work.description.label')"
                :placeholder="t('activity-tracker.simple.work.description.placeholder')"
                :maxlength="5120"
            />
        </template>
    </VJoyPanel>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {between, required, withMessage} from '@regle/rules';
    import {getDaysInMonth} from 'date-fns';
    import {storeToRefs} from 'pinia';
    import {useTranslation, useFormat} from '#imports';
    import {useScopedRegle} from '@malt/forms';
    import {VJoyFormError, VJoyInput, VJoyPanel, VJoyWrapper, VJoyTextarea} from '@maltjoy/core-vue';
    import {TemporalUnitResource} from '@api';
    import {useActivityTrackerStore, useEarningsOfTheMonthStore} from '@/common/stores';
    import {halfHourIncrement} from '@/common/validators';

    const {t} = useTranslation();
    const {formatAmount, getCurrencySymbolFrom} = useFormat();
    const activityTrackerStore = useActivityTrackerStore();
    const {setSimpleReportTotalWorkAmount} = activityTrackerStore;
    const {simpleReportTotalWorkAmount, month, year, unit, workDescription} = storeToRefs(activityTrackerStore);
    const earningsOfTheMonthStore = useEarningsOfTheMonthStore();
    const {
        dailyRate,
        amountExcludingServiceFeesIncludingTaxes,
        freelanceFeesExcludingTaxes,
        freelanceFeesTaxes,
        totalAmountExcludingTaxes,
        totalAmountIncludingTaxes,
        totalAmountTaxes,
        currency,
        freelancerCurrency,
        totalAmountInFreelancerCurrency,
        estimationExchangeRate,
        taxesValues,
        taxesWithAmountValues,
        freelancerFeeTaxPercent,
    } = storeToRefs(earningsOfTheMonthStore);

    const HOURS_IN_DAY = 24;

    const vatRate = computed(() => taxesValues.value?.find((item) => item.type === 'VAT')?.rate ?? 0);
    const feeVatRate = computed(() => freelancerFeeTaxPercent.value ?? 0);

    const isInDays = computed(() => unit.value === TemporalUnitResource.Day);

    const label = computed(() =>
        isInDays.value ? t('activity-tracker.simple.number.of.days.worked') : t('activity-tracker.simple.number.of.hours.worked'),
    );

    const maxTotalWorkAmount = computed(() => {
        const dayInMonth = getDaysInMonth(new Date(year.value, month.value - 1));
        const maxWorkAmountForOneMonth = isInDays.value ? dayInMonth : dayInMonth * HOURS_IN_DAY;

        return 3 * maxWorkAmountForOneMonth; // let's say freelancers can catch up 3 months into 1 month...
    });

    const simpleReportTotalWorkAmountModel = computed<number | undefined>({
        get() {
            return simpleReportTotalWorkAmount.value;
        },
        set(value) {
            const parsedValue = value == null ? Number.NaN : Number.parseFloat(String(value));

            if (Number.isNaN(parsedValue)) {
                setSimpleReportTotalWorkAmount(undefined);
                return;
            }

            setSimpleReportTotalWorkAmount(parsedValue);
        },
    });

    const rules = computed(() => ({
        required,
        between: between(0, maxTotalWorkAmount),
        halfHourIncrement,
    }));

    const {r$} = useScopedRegle(simpleReportTotalWorkAmountModel, rules);

    defineExpose({
        simpleReportTotalWorkAmount,
        r$,
    });
</script>

<style lang="scss" scoped>
    .simple-report {
        &__input {
            margin-top: 0;
            margin-bottom: var(--joy-core-spacing-6);

            :deep(.joy-input--wrapper-margin) {
                margin-left: 0;
                margin-bottom: var(--joy-core-spacing-2);
            }
            :deep(input) {
                font-weight: var(--joy-font-weight-normal);
                width: 267px;
                font-size: var(--joy-font-size-primary-300);
            }
            :deep(label) {
                font-weight: var(--joy-font-weight-bold);
            }
        }

        &__body-wrapper {
            margin-bottom: 0;
            padding-right: var(--joy-core-spacing-2);
            font-size: var(--joy-font-size-primary-300);
        }

        &__divider {
            border: 1px solid var(--joy-color-neutral-30);
        }

        &__total-earnings {
            font-size: var(--joy-font-size-primary-300);
        }

        &__earnings {
            font-weight: var(--joy-font-weight-bold);
            font-size: var(--joy-font-size-primary-400);
            line-height: var(--joy-line-height-large);
            letter-spacing: var(--joy-font-letter-spacing-tight);
        }

        &__earnings-info {
            font-weight: var(--joy-font-weight-normal);
            line-height: var(--joy-line-height-large);
            color: var(--joy-color-neutral-50);
            font-size: var(--joy-font-size-primary-300);
        }

        &__multi-currency {
            display: flex;
            justify-content: space-between;
        }
    }

    .taxes-breakdown {
        color: var(--joy-color-neutral-40);

        &-total {
            font-weight: var(--joy-font-weight-bold);
        }
    }

    .total-including-taxes {
        font-weight: var(--joy-font-weight-bold);
        color: var(--joy-color-secondary-50);
    }

    .total-wrapper {
        gap: 10px;
        font-weight: var(--joy-font-weight-bold);
        background-color: var(--joy-color-secondary-10);
        border-radius: var(--joy-core-radius-2);
        padding: var(--joy-core-spacing-3) var(--joy-core-spacing-2) var(--joy-core-spacing-3) var(--joy-core-spacing-3);
        color: var(--joy-color-secondary-50);
    }

    .invoicedInCurrency-wrapper {
        border-radius: var(--joy-core-radius-2);
        font-weight: var(--joy-font-weight-bold);
        color: var(--joy-color-secondary-50);
    }
</style>
